import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Clock, Wrench, Users, MessageSquareWarning, Building2,
  LogOut, User, Menu, X, Home, TrendingUp, Package, Loader2, IndianRupee,
  FileText, Stamp, Receipt, ClipboardCheck
} from 'lucide-react';

// Import mobile pages
import MobileCheckIn from './pages/MobileCheckIn';
import MobileServices from './pages/MobileServices';
import MobileLeads from './pages/MobileLeads';
import MobileComplaints from './pages/MobileComplaints';
import MobileCustomers from './pages/MobileCustomers';
import MobileInventory from './pages/MobileInventory';
import MobilePendingPayments from './pages/MobilePendingPayments';
import MobileMyPendingPayments from './pages/MobileMyPendingPayments';
import MobileContracts from './pages/MobileContracts';
import MobileStamping from './pages/MobileStamping';
import MobileExpenses from './pages/MobileExpenses';
import MobileServiceApproval from './pages/MobileServiceApproval';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Persistent storage keys
const STORAGE_KEYS = {
  TOKEN: 'suntech_mobile_token',
  USER: 'suntech_mobile_user',
  SESSION: 'suntech_mobile_session'
};

const MobileApp = () => {
  const { user: authUser, token: authToken, logout: authLogout, login: authLogin } = useAuth();
  const [activePage, setActivePage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  
  // Local state for persistent session
  const [localToken, setLocalToken] = useState(null);
  const [localUser, setLocalUser] = useState(null);

  // Use either auth context or local storage
  const token = authToken || localToken;
  const user = authUser || localUser;

  // Initialize from persistent storage on mount - NEVER auto-logout
  useEffect(() => {
    const initializeSession = () => {
      const savedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
      
      // Also check main auth storage
      const mainToken = localStorage.getItem('token');
      const mainUser = localStorage.getItem('user');
      
      const tokenToUse = savedToken || mainToken;
      const userToUse = savedUser || mainUser;
      
      if (tokenToUse && userToUse) {
        try {
          const parsedUser = JSON.parse(userToUse);
          setLocalToken(tokenToUse);
          setLocalUser(parsedUser);
          
          // Sync to both storage locations for persistence
          localStorage.setItem(STORAGE_KEYS.TOKEN, tokenToUse);
          localStorage.setItem(STORAGE_KEYS.USER, userToUse);
          localStorage.setItem('token', tokenToUse);
          localStorage.setItem('user', userToUse);
        } catch (e) {
          console.error('Error parsing saved user:', e);
          // Don't clear storage on parse error - just log it
        }
      }
      setInitializing(false);
    };
    
    initializeSession();
  }, []);

  const validateToken = async (tokenToValidate) => {
    // Token validation is only used for API calls, not for session management
    // Session persists until explicit logout
    try {
      const response = await fetch(`${API_URL}/api/employees/my-roles`, {
        headers: { Authorization: `Bearer ${tokenToValidate}` }
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const clearLocalStorage = () => {
    // Clear all possible storage keys on explicit logout only
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setLocalToken(null);
    setLocalUser(null);
  };

  // Check if user has specific role access
  const isServiceEngineer = user?.role === 'Service Engineer';
  const isSalesMarketing = user?.role === 'Sales & Marketing';
  const isBDMSE = user?.role === 'BDM & SE';
  const isBackOffice = user?.role === 'Back Office Executive';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const isAdmin = user?.role === 'Admin';

  // Mobile App Access Control based on user requirements:
  // - Service Engineer: Services (own only), Complaints (create)
  // - Sales & Marketing: Leads (own only), Complaints (create)
  // - BDM & SE: Services (all view, own edit), Leads (own), Customers (own created), Complaints (all)
  // - Back Office Executive: Leads, Services, Complaints, Customers, Sales, Contracts, Stamping, Attendance
  // - Backoffice Accounts Executive: Sales(Pending), Leads, Services, Customers, Contracts, Stamping, Complaints, Expenses, Attendance
  // - Admin: Full mobile access
  
  // Determine available features based on role
  const canAccessServices = isServiceEngineer || isBDMSE || isBackOffice || isBackOfficeAccounts || isAdmin;
  const canAccessLeads = isSalesMarketing || isBDMSE || isBackOffice || isBackOfficeAccounts || isAdmin;
  const canAccessComplaints = isServiceEngineer || isSalesMarketing || isBDMSE || isBackOffice || isBackOfficeAccounts || isAdmin;
  const canAccessCustomers = isBDMSE || isBackOffice || isBackOfficeAccounts || isAdmin;
  const canAccessInventory = isAdmin; // Only Admin on mobile
  const canAccessSales = isBackOffice || isBackOfficeAccounts || isAdmin; // Pending payments
  const canAccessMyPayments = isServiceEngineer || isBDMSE; // Engineer's own pending collections
  const canAccessContracts = isBackOffice || isBackOfficeAccounts || isAdmin;
  const canAccessStamping = isBackOffice || isBackOfficeAccounts || isAdmin;
  const canAccessExpenses = isBackOfficeAccounts || isAdmin;
  const canAccessServiceApprovals = isBackOfficeAccounts || isAdmin; // Service Approvals

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Save to persistent storage
        localStorage.setItem(STORAGE_KEYS.TOKEN, data.access_token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
        localStorage.setItem(STORAGE_KEYS.SESSION, new Date().toISOString());
        
        // Update local state
        setLocalToken(data.access_token);
        setLocalUser(data.user);
        
        // Also update main auth context
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        toast.success(`Welcome, ${data.user.name}!`);
        setLoginForm({ username: '', password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed. Please check your connection.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all storage
    clearLocalStorage();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Call auth logout if available
    if (authLogout) {
      authLogout();
    }
    
    setActivePage('home');
    setMenuOpen(false);
    toast.success('Logged out successfully');
  };

  // Get navigation items based on role
  const getNavItems = () => {
    const items = [
      { id: 'home', icon: Home, label: 'Home', show: true, color: 'text-slate-600' },
      { id: 'checkin', icon: Clock, label: 'Attendance', show: true, color: 'text-emerald-600' },
      { id: 'services', icon: Wrench, label: 'Services', show: canAccessServices, color: 'text-blue-600' },
      { id: 'serviceApprovals', icon: ClipboardCheck, label: 'Approvals', show: canAccessServiceApprovals, color: 'text-violet-600' },
      { id: 'leads', icon: TrendingUp, label: 'Leads', show: canAccessLeads, color: 'text-purple-600' },
      { id: 'complaints', icon: MessageSquareWarning, label: 'Complaints', show: canAccessComplaints, color: 'text-orange-600' },
      { id: 'sales', icon: IndianRupee, label: 'Payments', show: canAccessSales, color: 'text-green-600' },
      { id: 'myPayments', icon: IndianRupee, label: 'My Payments', show: canAccessMyPayments, color: 'text-green-600' },
      { id: 'contracts', icon: FileText, label: 'Contracts', show: canAccessContracts, color: 'text-indigo-600' },
      { id: 'stamping', icon: Stamp, label: 'Stamping', show: canAccessStamping, color: 'text-pink-600' },
      { id: 'expenses', icon: Receipt, label: 'Expenses', show: canAccessExpenses, color: 'text-red-600' },
      { id: 'inventory', icon: Package, label: 'Inventory', show: canAccessInventory, color: 'text-teal-600' },
      { id: 'customers', icon: Building2, label: 'Customers', show: canAccessCustomers, color: 'text-cyan-600' },
    ];
    return items.filter(item => item.show);
  };

  // Show loading spinner while initializing
  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login Screen - Modern Design
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-6">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Wrench className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">SUNTECH</h1>
          <p className="text-slate-400">Field Operations</p>
        </div>
        
        {/* Login Card */}
        <Card className="w-full max-w-sm bg-slate-800/50 border-slate-700 backdrop-blur-xl">
          <CardContent className="p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Username / Mobile"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold shadow-lg shadow-emerald-500/30"
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-slate-500 text-sm mt-8">SUNTECH ENTERPRISES</p>
      </div>
    );
  }

  // Render Home Dashboard JSX
  const homePageContent = (
    <div className="p-4 space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur">
            <User className="w-8 h-8" />
          </div>
          <div>
            <p className="text-emerald-100 text-sm">Welcome back,</p>
            <h2 className="text-xl font-bold">{user?.name || 'User'}</h2>
            <p className="text-emerald-100 text-sm">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div>
        <h3 className="text-slate-600 font-semibold mb-3 px-1">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-emerald-50 to-emerald-100 active:scale-95"
            onClick={() => setActivePage('checkin')}
          >
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <p className="font-semibold text-slate-700">Attendance</p>
              <p className="text-xs text-slate-500 mt-1">Check In/Out</p>
            </CardContent>
          </Card>

          {canAccessServices && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-blue-50 to-blue-100 active:scale-95"
              onClick={() => setActivePage('services')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Services</p>
                <p className="text-xs text-slate-500 mt-1">Manage Jobs</p>
              </CardContent>
            </Card>
          )}

          {canAccessLeads && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-purple-50 to-purple-100 active:scale-95"
              onClick={() => setActivePage('leads')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-purple-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Leads</p>
                <p className="text-xs text-slate-500 mt-1">Sales Pipeline</p>
              </CardContent>
            </Card>
          )}

          {canAccessComplaints && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-orange-50 to-orange-100 active:scale-95"
              onClick={() => setActivePage('complaints')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-orange-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-orange-500/30">
                  <MessageSquareWarning className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Complaints</p>
                <p className="text-xs text-slate-500 mt-1">Issue Tracking</p>
              </CardContent>
            </Card>
          )}

          {canAccessSales && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-green-50 to-green-100 active:scale-95"
              onClick={() => setActivePage('sales')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-green-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-green-500/30">
                  <IndianRupee className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Payments</p>
                <p className="text-xs text-slate-500 mt-1">Pending Payments</p>
              </CardContent>
            </Card>
          )}

          {canAccessMyPayments && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-green-50 to-green-100 active:scale-95"
              onClick={() => setActivePage('myPayments')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-green-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-green-500/30">
                  <IndianRupee className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">My Payments</p>
                <p className="text-xs text-slate-500 mt-1">Pending Collections</p>
              </CardContent>
            </Card>
          )}

          {canAccessContracts && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-indigo-50 to-indigo-100 active:scale-95"
              onClick={() => setActivePage('contracts')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-indigo-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Contracts</p>
                <p className="text-xs text-slate-500 mt-1">AMC / MMC</p>
              </CardContent>
            </Card>
          )}

          {canAccessStamping && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-pink-50 to-pink-100 active:scale-95"
              onClick={() => setActivePage('stamping')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-pink-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-pink-500/30">
                  <Stamp className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Stamping</p>
                <p className="text-xs text-slate-500 mt-1">Licenses</p>
              </CardContent>
            </Card>
          )}

          {canAccessExpenses && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-red-50 to-red-100 active:scale-95"
              onClick={() => setActivePage('expenses')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-red-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-red-500/30">
                  <Receipt className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Expenses</p>
                <p className="text-xs text-slate-500 mt-1">Manage</p>
              </CardContent>
            </Card>
          )}

          {canAccessInventory && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-teal-50 to-teal-100 active:scale-95"
              onClick={() => setActivePage('inventory')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-teal-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Inventory</p>
                <p className="text-xs text-slate-500 mt-1">Stock View</p>
              </CardContent>
            </Card>
          )}

          {canAccessCustomers && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-gradient-to-br from-cyan-50 to-cyan-100 active:scale-95"
              onClick={() => setActivePage('customers')}
            >
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-slate-700">Customers</p>
                <p className="text-xs text-slate-500 mt-1">Client Info</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );

  // Render active page
  const renderPage = () => {
    switch (activePage) {
      case 'checkin':
        return <MobileCheckIn />;
      case 'services':
        return canAccessServices ? <MobileServices /> : homePageContent;
      case 'serviceApprovals':
        return canAccessServiceApprovals ? <MobileServiceApproval /> : homePageContent;
      case 'leads':
        return canAccessLeads ? <MobileLeads /> : homePageContent;
      case 'complaints':
        return canAccessComplaints ? <MobileComplaints /> : homePageContent;
      case 'sales':
        return canAccessSales ? <MobilePendingPayments /> : homePageContent;
      case 'myPayments':
        return canAccessMyPayments ? <MobileMyPendingPayments /> : homePageContent;
      case 'contracts':
        return canAccessContracts ? <MobileContracts /> : homePageContent;
      case 'stamping':
        return canAccessStamping ? <MobileStamping /> : homePageContent;
      case 'expenses':
        return canAccessExpenses ? <MobileExpenses /> : homePageContent;
      case 'inventory':
        return canAccessInventory ? <MobileInventory /> : homePageContent;
      case 'customers':
        return canAccessCustomers ? <MobileCustomers /> : homePageContent;
      default:
        return homePageContent;
    }
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header - Modern Design */}
      <header className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 sticky top-0 z-50 shadow-lg">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(!menuOpen)} 
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div>
              <h1 className="text-lg font-bold">
                {activePage === 'home' ? 'SUNTECH' : navItems.find(n => n.id === activePage)?.label || 'SUNTECH'}
              </h1>
              {activePage === 'home' && <p className="text-xs text-slate-400">Field Operations</p>}
            </div>
          </div>
          <button 
            onClick={handleLogout} 
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Side Menu - Modern Design */}
      {menuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div 
            className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl" 
            onClick={e => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                  <User className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">{user?.name}</p>
                  <p className="text-sm text-slate-400">{user?.role}</p>
                </div>
              </div>
            </div>
            
            {/* Menu Items */}
            <nav className="p-3">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setActivePage(item.id); setMenuOpen(false); }}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl text-left mb-1 transition-all ${
                    activePage === item.id 
                      ? 'bg-emerald-50 text-emerald-700 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${activePage === item.id ? 'text-emerald-600' : item.color}`} />
                  <span>{item.label}</span>
                </button>
              ))}
              
              <hr className="my-3 border-slate-200" />
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-4 p-4 rounded-xl text-left text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="min-h-[calc(100vh-140px)]">
        {renderPage()}
      </main>

      {/* Bottom Navigation - Modern Design */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-2xl z-30 safe-area-pb">
        <div className="flex justify-around">
          {navItems.slice(0, 5).map(item => (
            <button
              key={item.id}
              onClick={() => setActivePage(item.id)}
              className={`flex-1 py-3 flex flex-col items-center transition-all ${
                activePage === item.id 
                  ? 'text-emerald-600' 
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <div className={`p-1 rounded-lg ${activePage === item.id ? 'bg-emerald-50' : ''}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <span className={`text-xs mt-1 ${activePage === item.id ? 'font-medium' : ''}`}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default MobileApp;
